import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-sand/20">
      {/* Header */}
      <div className="bg-forest text-white py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors text-sm">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <h1 className="font-heading text-3xl md:text-4xl text-forest mb-2">
            Privacy Policy
          </h1>
          
          <div className="text-gray-600 mb-8 space-y-1">
            <p><strong>Effective Date:</strong> February 23, 2026</p>
            <p><strong>Owned and Operated By:</strong> Volition Enterprises, Inc.</p>
            <p><strong>Physical Address:</strong> 3501 Browns Mill Road, Atlanta, Georgia 30354</p>
            <p><strong>Contact Email:</strong> info@volitionenterprises.com</p>
          </div>

          <div className="bg-terracotta/10 border border-terracotta/20 rounded-xl p-4 mb-8">
            <p className="text-sm text-forest">
              This Privacy Policy is Section 29 of the Sister Diamonds App Master Terms of Service. 
              For the complete agreement, please visit our <Link to="/terms" className="text-terracotta font-medium hover:underline">Terms of Service</Link>.
            </p>
          </div>

          <hr className="my-8 border-sand" />

          <div className="prose prose-forest max-w-none">
            <h2 className="font-heading text-2xl text-forest mt-8 mb-4">PRIVACY POLICY</h2>

            <h3 className="font-heading text-xl text-forest mt-8 mb-3">1. Information We Collect</h3>
            <p>We collect account information, subscription data, community posts, private reflections, SHE interactions, and usage analytics.</p>

            <h3 className="font-heading text-xl text-forest mt-8 mb-3">2. How We Use Information</h3>
            <p>Information is used to operate Services, personalize experiences, process payments, and comply with legal obligations. We do not sell personal information.</p>

            <h3 className="font-heading text-xl text-forest mt-8 mb-3">3. AI Data Usage</h3>
            <p>SHE interactions may be processed using secure AI infrastructure. Private data is not used to train public AI models.</p>

            <h3 className="font-heading text-xl text-forest mt-8 mb-3">4. Data Security</h3>
            <p>We implement reasonable safeguards but cannot guarantee absolute security. The Company does not guarantee that the Services will be free from unauthorized access, hacking, data loss, or other security incidents, and you acknowledge and accept these inherent risks of online services.</p>

            <h3 className="font-heading text-xl text-forest mt-8 mb-3">5. Data Retention</h3>
            <p>Data is retained as necessary for operations, legal compliance, and security.</p>

            <h3 className="font-heading text-xl text-forest mt-8 mb-3">6. Credit Card Storage</h3>
            <p>We may securely store your payment method through authorized payment processors for subscription and approved charges. We do not directly store full credit card numbers.</p>

            <h3 className="font-heading text-xl text-forest mt-8 mb-3">7. Notice at Collection</h3>
            <p>At or before the time of data collection, users are informed of:</p>
            <ul>
              <li>Categories of personal information collected</li>
              <li>Purpose of collection</li>
              <li>Retention period criteria</li>
              <li>Whether data is sold or shared (we do not sell personal data)</li>
            </ul>

            <h3 className="font-heading text-xl text-forest mt-8 mb-3">8. International Data Transfer</h3>
            <p>If you access the Services outside the United States, you consent to the transfer and processing of your data in the United States.</p>

            <h3 className="font-heading text-xl text-forest mt-8 mb-3">9. Third-Party Processors</h3>
            <p>The Company may use service providers to process data, including:</p>
            <ul>
              <li>Payment processors</li>
              <li>Hosting providers</li>
              <li>AI infrastructure providers</li>
            </ul>
            <p>Such providers process data solely for authorized business purposes.</p>

            <h3 className="font-heading text-xl text-forest mt-8 mb-3">10. Data Responsibility for Coaches</h3>
            <h4 className="font-heading text-lg text-forest mt-6 mb-2">10.1 Coach Data Handling Obligations</h4>
            <p>Coaches agree to:</p>
            <ul>
              <li>Maintain confidentiality of client information</li>
              <li>Use the App only for authorized purposes</li>
              <li>Not export client data without lawful basis</li>
              <li>Comply with applicable privacy laws</li>
            </ul>
            <p>The Company reserves the right to suspend coach access for data misuse.</p>

            <h3 className="font-heading text-xl text-forest mt-8 mb-3">11. Member Content Disclaimer</h3>
            <p>Private reflections are not visible to other Members by default. However:</p>
            <ul>
              <li>The Company may access content for technical support, legal compliance, or fraud prevention.</li>
              <li>The Company may remove content that violates policy.</li>
            </ul>

            <h3 className="font-heading text-xl text-forest mt-8 mb-3">12. California and GDPR Rights</h3>
            <p>California residents may request access, deletion, and correction of personal information.</p>

            <h3 className="font-heading text-xl text-forest mt-8 mb-3">13. Communication Consent</h3>
            <p>By enrolling, you expressly consent to receive:</p>
            <ul>
              <li>Transactional emails</li>
              <li>Account notifications</li>
              <li>Marketing emails (where permitted)</li>
              <li>SMS messages (if opted in)</li>
            </ul>
            <p>You may revoke consent:</p>
            <ul>
              <li>By clicking unsubscribe in emails</li>
              <li>By replying STOP to SMS</li>
              <li>By adjusting preferences in the App</li>
            </ul>
            <p>Standard message and data rates may apply. Consent to receive SMS messages is not a condition of purchase of any goods or services.</p>

            <h3 className="font-heading text-xl text-forest mt-8 mb-3">14. Contact Information</h3>
            <div className="bg-sand/30 rounded-xl p-6 mt-4">
              <p className="font-semibold text-forest">Volition Enterprises, Inc.</p>
              <p>3501 Browns Mill Road</p>
              <p>Atlanta, Georgia 30354</p>
              <p className="mt-2">
                <a href="mailto:info@volitionenterprises.com" className="text-terracotta hover:underline">
                  info@volitionenterprises.com
                </a>
              </p>
            </div>
          </div>

          <hr className="my-8 border-sand" />

          <div className="text-center text-gray-500 text-sm">
            <p>Last Updated: February 23, 2026</p>
            <p className="mt-2">
              <Link to="/terms" className="text-terracotta hover:underline">View Full Terms of Service</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
